import {Injectable} from '@angular/core';
import {AngularFireDatabase} from 'angularfire2/database';
import {Observable} from 'rxjs/Observable';
import {Credential, Project} from "./model/projects.model";
import {AngularFirestore, AngularFirestoreCollection} from "angularfire2/firestore";
import * as firebase from 'firebase';
import {Service} from "./model/service.models";
import OrderByDirection = firebase.firestore.OrderByDirection;
import {Filter} from './model/filter.model';

@Injectable()
export class ProjectService {

    constructor(
        private fireDB: AngularFireDatabase,
        private afs: AngularFirestore) {
    }

    /**
     * create a project
     * @param {Project} item
     * @returns {Promise<firebase.firestore.DocumentReference>}
     */
    createProject(item: Project) {
        item.created_at = new Date().getTime();
        item.id = this.afs.createId();
        return this.afs.collection<any>('projects').add(JSON.parse(JSON.stringify(item)));
    }

    /**
     * Add Credentials
     * @param {string} key
     * @param {Credential} item
     * @returns {Promise<firebase.firestore.DocumentReference>}
     */
    addCredential(key: string, item: Credential) {
        item['created_at'] = new Date().getTime();
        return this.afs.collection<any>('projects/' + key + '/credentials').add(JSON.parse(JSON.stringify(item)));
    }

    /**
     * Add 3rd Party
     * @param {string} key
     * @param {Credential} item
     * @returns {Promise<firebase.firestore.DocumentReference>}
     */
    addThirdParty(key: string, item: Credential) {
        item['created_at'] = new Date().getTime();
        const path = 'projects/' + key + '/third_party';

        return this.afs.collection<any>(path).add(JSON.parse(JSON.stringify(item)));
    }

    /**
     * Add service
     * @param {string} key
     * @param {Service} item
     * @returns {Promise<firebase.firestore.DocumentReference>}
     */
    addService(key: string, item: Service) {
        item['created_at'] = new Date().getTime();
        return this.afs.collection<any>('projects/' + key + '/outsourcing').add(JSON.parse(JSON.stringify(item)));
    }

    /**
     * Delete credentials
     * @param {string} id
     * @param {string} key
     * @returns {Promise<void>}
     */
    deleteCredential(id: string, key: string){
        return this.afs.collection<any>('projects/' + id + '/credentials').doc(key).delete();
    }

    /**
     * Delete 3rd party
     * @param {string} id
     * @param {string} key
     * @returns {Promise<void>}
     */
    deletethirdParty(id: string, key: string){
        return this.afs.collection<any>('projects/' + id + '/third_party').doc(key).delete();
    }

    /**
     * Delete service
     * @param {string} id
     * @param {string} key
     * @returns {Promise<void>}
     */
    deleteService(id: string, key: string){
        return this.afs.collection<any>('projects/' + id + '/outsourcing').doc(key).delete();
    }

    /**
     * update a project
     * @param {string} key
     * @param {Project} item
     * @param {boolean} stringify
     * @returns {Promise<void>}
     */
    updateProject(key: string, item: Project, stringify?: boolean) {
        if(stringify){
            return this.afs.collection('projects').doc(key).update(JSON.parse(JSON.stringify(item)));
        }
        return this.afs.collection('projects').doc(key).update(item);
    }

    /**
     * update credentials
     * @param {string} id
     * @param {string} key
     * @param item
     * @param {boolean} stringify
     * @returns {Promise<void>}
     */
    updateCredential(id: string, key: string, item: any, stringify?: boolean) {
        delete item.key;
        const path = 'projects/' + id + '/credentials';

        if(stringify){
            return this.afs.collection<any>(path).doc(key).update(JSON.parse(JSON.stringify(item)));
        }
        return this.afs.collection(path).doc(key).update(item);
    }

    /**
     * updte 3rd party
     * @param {string} id
     * @param {string} key
     * @param item
     * @param {boolean} stringify
     * @returns {Promise<void>}
     */
    updateThirdParty(id: string, key: string, item: any, stringify?: boolean) {
        delete item.key;
        const path = 'projects/' + id + '/third_party';

        if(stringify){
            return this.afs.collection<any>(path).doc(key).update(JSON.parse(JSON.stringify(item)));
        }
        return this.afs.collection(path).doc(key).update(item);
    }

    /**
     * update service
     * @param {string} id
     * @param {string} key
     * @param item
     * @param {boolean} stringify
     * @returns {Promise<void>}
     */
    updateService(id: string, key: string, item: any, stringify?: boolean) {
        delete item.key;
        const path = 'projects/' + id + '/outsourcing';

        if(stringify){
            return this.afs.collection<any>(path).doc(key).update(JSON.parse(JSON.stringify(item)));
        }
        return this.afs.collection(path).doc(key).update(item);
    }

    /**
     * get project credentials
     * @param {string} key
     * @returns {Observable<DocumentChangeAction[]>}
     */
    getProjectCredentials(key: string){
        return this.afs.collection('projects/' + key + '/credentials', ref => {
            return ref.orderBy('created_at', 'asc');
        }).snapshotChanges();
    }

    /**
     * get project 3rd party
     * @param {string} key
     * @returns {Observable<DocumentChangeAction[]>}
     */
    getProjectThirdParties(key: string){
        return this.afs.collection('projects/' + key + '/third_party', ref => {
            return ref.orderBy('created_at', 'asc');
        }).snapshotChanges();
    }

    /**
     * Get project outsourcing
     * @param {string} key
     * @returns {Observable<DocumentChangeAction[]>}
     */
    getProjectOutsourcing(key: string){
        return this.afs.collection('projects/' + key + '/outsourcing', ref => {
            return ref.orderBy('created_at', 'asc');
        }).snapshotChanges();
    }

    /**
     * get projects by category
     * @returns {Observable<any[]>}
     */
    getProjectsByCat(sortBy: string, OrderStatus: OrderByDirection, filter?: Filter): Observable<any[]> {
        return this.afs.collection('projects', ref => {
            let query = ref.orderBy(sortBy, OrderStatus);

            // TODO Need to automate index creations from Firestore Console, all if statements here are correct, since Firestore does not allow to create auto indexes, plus the usage limit of query on documents, I'm going to put these filters on the frontend till we get a proper solution from Firebase Team
            // https://firebase.google.com/docs/firestore/query-data/indexing
            // https://stackoverflow.com/questions/47960930/how-to-automatically-create-indexes-for-cloud-firestore
            if(filter.platform && typeof filter.platform == 'number') {
                console.log('filtering with ', filter.platform);
                query = query.where('platform', '==', filter.platform)
            }

            if(filter.is_designed) {
                console.log('filtering with ', filter.is_designed);
                query = query.where('is_designed', '==', filter.is_designed)
            }

            if(filter.is_developed) {
                console.log('filtering with ', filter.is_developed);
                query = query.where('is_developed', '==', filter.is_developed)
            }

            if(filter.is_released) {
                console.log('filtering with ', filter.is_released);
                query = query.where('is_released', '==', filter.is_released)
            }

            if(filter.is_content) {
                console.log('filtering with ', filter.is_content);
                query = query.where('is_content', '==', filter.is_content)
            }

            return query;
        }).snapshotChanges();
    }

    /**
     * get project by key
     * @param {string} id
     * @returns {Observable<any>}
     */
    getProject(id: string): Observable<any>{
        return this.afs.doc('projects/' + id).valueChanges();
    }

    /**
     * get project current url, depends on the status of a project (prod, env or staging)
     * @param {Project} project
     * @returns {any}
     */
    getProjectUrl(project: Project){
        if(project.url == null || project.url == undefined){
            return '';
        }
        return project.url[this.getProjectStatus(project)]
    }

    /**
     * get project status
     * @param {Project} project
     * @returns {string}
     */
    getProjectStatus(project: Project){
        if(project['is_released']){
            return 'production'
        }else if(project['is_developed']){
            return 'staging';
        }else{
            return 'development'
        }
    }


}



// WEBPACK FOOTER //
// /Users/moe.dchater/personal/firebase-projects/hosting/src/app/services/project.service.ts